import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeedbackOptions } from './constants';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import { ViewWrapper } from '../ui-components/view-wrapper.component';
import SubmitButtonFrame from './submit-button-frame.component';
import { Box, Typography, FormControlLabel, TextField } from '@mui/material';

import { usePostHog } from 'posthog-js/react';
import { ColorType } from '../../assets/colors/colors.g';
import CustomCheckbox from '../ui-components/checkbox.component';

const MAX_COMMENT_LEN = 160;

interface FeedbackProps {
  selectedFeedback: FeedbackOptions;
  startTime: string;
  endTime: string;
  location: string;
  chargerName: string;
  energyDelivered: string;
  cost: string;
  transactionId: string;
  onClose: () => void;
  onSubmit: () => void;
}

const Feedback: React.FC<FeedbackProps> = ({
  selectedFeedback,
  startTime,
  endTime,
  location,
  chargerName,
  energyDelivered,
  cost,
  transactionId,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [issues, setIssues] = useState<string[]>([]);
  const [additionalComments, setAdditionalComments] = useState('');
  const posthog = usePostHog();

  const handleIssueToggle = (issue: string) => {
    setIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue],
    );
  };

  const feedbackCheckBoxTitle = useMemo(() => {
    if (selectedFeedback === FeedbackOptions.Happy) {
      return t('feedback_checkbox_title_happy');
    } else {
      return t('feedback_checkbox_title_unhappy_average');
    }
  }, [selectedFeedback, t]);

  const feedbackReasons = useMemo(() => {
    return selectedFeedback === FeedbackOptions.Happy
      ? []
      : [
          'feedback_reason_start_charge',
          'feedback_reason_location',
          'feedback_reason_malfunction',
          'feedback_reason_app',
        ];
  }, [selectedFeedback]);

  const handleSubmit = () => {
    posthog.capture('session_feedback', {
      transactionId: transactionId,
      charger: chargerName,
      startTime: startTime,
      endTime: endTime,
      location: location,
      energyDelivered: energyDelivered,
      cost: cost,
      satisfaction: selectedFeedback.toString(),
      reasons: issues,
      comments: additionalComments,
    });
    onSubmit();
  };

  return (
    <ViewWrapper>
      <BackButtonHeader title={t('feedback_title')} onBackArrowClicked={onClose} />
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          maxWidth: 425,
          mx: 'auto',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '28px',
            textAlign: 'center',
            color: ColorType.GREY6,
            marginBottom: '16px',
          }}
          align='center'
        >
          {t('feedback_how_today')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '26px',
            textAlign: 'center',
            whiteSpace: 'pre-line',
            color: ColorType.BLACK,
          }}
        >
          {feedbackCheckBoxTitle}
        </Typography>
        {feedbackReasons.map((issue) => (
          <FormControlLabel
            key={issue}
            control={
              <CustomCheckbox
                color='primary'
                checked={issues.includes(issue)}
                onChange={() => handleIssueToggle(issue)}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  textAlign: 'left',
                  color: '#202223',
                }}
              >
                {t(issue)}
              </Typography>
            }
          />
        ))}
        <TextField
          multiline
          rows={4}
          placeholder={t('feedback_additional_comments')}
          value={additionalComments}
          onChange={(e) => {
            const input = e.target.value;
            if (input.length <= MAX_COMMENT_LEN) {
              setAdditionalComments(e.target.value);
            }
          }}
          sx={{
            mt: '8px',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            textAlign: 'left',
            color: '#B0B8C1',
            background: '#F2F4F6',
            width: '100%',
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#202223', // change to your desired color
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
            },
          }}
        />
        <Typography
          align='right'
          sx={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
            textAlign: 'left',
            color: '#6B7684',
          }}
        >
          {`${additionalComments.length}/${MAX_COMMENT_LEN}`}
        </Typography>
      </Box>
      <SubmitButtonFrame onSubmit={handleSubmit} />
    </ViewWrapper>
  );
};

export default Feedback;
